import { useEffect, useRef } from 'react'
import Paho from 'paho-mqtt'

const HOST = process.env.REACT_APP_MQTT_HOST || 'api.menu-rest.com'
const USERNAME = process.env.REACT_APP_MQTT_USERNAME || 'react'
const PASSWORD = process.env.REACT_APP_MQTT_PASSWORD
const REFRESH_DEBOUNCE = 2000
const POLL_INTERVAL = 90000

// Подписка на /api/menu/{restId}: событие stoplist применяется точечно
// (onStoplistChange), событие refresh перечитывает меню целиком с debounce.
// MQTT-сессия может молча отвалиться (сон устройства, смена сети), поэтому
// поверх держим периодический опрос и рефетч при возврате вкладки в фокус.
export default function useMenuSync(restId, { onRefresh, onStoplistChange }) {
    const refreshTimer = useRef(null)
    const onRefreshRef = useRef(onRefresh)
    const onStoplistChangeRef = useRef(onStoplistChange)
    onRefreshRef.current = onRefresh
    onStoplistChangeRef.current = onStoplistChange

    useEffect(() => {
        if (!restId) return undefined

        // Опрос и рефетч-по-фокусу должны работать всегда, даже если MQTT недоступен —
        // это единственная защита от рассинхронизации, если пуш молча отвалился
        const pollId = setInterval(() => onRefreshRef.current?.(), POLL_INTERVAL)
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') onRefreshRef.current?.()
        }
        document.addEventListener('visibilitychange', onVisibilityChange)

        if (!PASSWORD) {
            console.warn('useMenuSync: REACT_APP_MQTT_PASSWORD is not set, menu updates will rely on polling only')
        }

        const scheduleRefresh = () => {
            clearTimeout(refreshTimer.current)
            refreshTimer.current = setTimeout(() => onRefreshRef.current?.(), REFRESH_DEBOUNCE)
        }

        let client = null
        if (PASSWORD) {
            client = new Paho.Client(`wss://${HOST}/ws`, `kiosk${Date.now()}`)

            const onConnect = () => {
                client.subscribe(`/api/menu/${restId}`, { qos: 0 })
                console.log(`menu mqtt connected, subscribed to /api/menu/${restId}`)
                onRefreshRef.current?.()
            }

            client.onConnectionLost = (responseObject) => {
                if (responseObject.errorCode !== 0) console.log('menu mqtt lost: ' + responseObject.errorMessage)
            }

            client.onMessageArrived = (message) => {
                let payload
                try {
                    payload = JSON.parse(message.payloadString)
                } catch (e) {
                    return
                }

                if (payload.event === 'stoplist') {
                    onStoplistChangeRef.current?.({
                        menuId: payload.menuId,
                        stopped: payload.stopped,
                        quantity: payload.quantity,
                        quantityLeft: payload.quantityLeft
                    })
                } else if (payload.event === 'refresh') {
                    scheduleRefresh()
                }
            }

            client.connect({
                timeout: 3,
                cleanSession: true,
                onSuccess: onConnect,
                onFailure: (err) => console.log('menu mqtt connect failed', err),
                keepAliveInterval: 20,
                userName: USERNAME,
                useSSL: true,
                password: PASSWORD
            })
        }

        return () => {
            clearInterval(pollId)
            clearTimeout(refreshTimer.current)
            document.removeEventListener('visibilitychange', onVisibilityChange)
            if (client) {
                try {
                    client.disconnect()
                } catch (e) {}
            }
        }
    }, [restId])
}
